import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit, Signal } from '@angular/core';
import { InputModule, RadioModule, SelectModule } from '@sag-ui';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ReferenceNumberInternationalFormGroup } from '../../../models/cod-create.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, debounceTime, distinctUntilChanged, filter, Observable, tap } from 'rxjs';
import { select, Store } from '@ngxs/store';
import { AddErrorMessage, LoadOrderLineItems, RemoveErrorMessage } from '../../../state/cod.action';
import { CodState } from '../../../state/cod.state';
import { ReferenceNumberTypeInternational } from '@cod-monorepo/interfaces';
import { NatureOfComplaintInternational } from '@cod-monorepo/interfaces';
import { WorkflowError } from '../../../../shared/enums/workflow-error';

@Component({
    selector: 'app-cod-reference-number-international-step',
    templateUrl: './reference-number-international.component.html',
    imports: [CommonModule, SelectModule, InputModule, FormsModule, ReactiveFormsModule, RadioModule],
    standalone: true
})
export class CodReferenceNumberInternationalStepComponent implements OnInit {
    @Input() referenceNumberInternationalForm: FormGroup<ReferenceNumberInternationalFormGroup> | undefined;

    public readonly isQueryingReferenceNumber: Signal<boolean> = select(CodState.queryingReferenceNumber);
    public referenceNumberFound$: Observable<boolean | null> = inject(Store).select(CodState.referenceNumberFound);

    public refernceNumberTypes = ReferenceNumberTypeInternational;
    public natureOfComplaints = NatureOfComplaintInternational;

    constructor(private destroyRef: DestroyRef, private store: Store) {}

    ngOnInit(): void {
        this.initReferenceNumberChanges();
        this.initProductComplaintChanges();
        this.initWithin40DaysChanges();
        this.initReferenceNumberFound();
    }

    private initReferenceNumberChanges(): void {
        if (this.referenceNumberInternationalForm?.disabled) {
            return;
        }

        const referenceNumberControl = this.referenceNumberInternationalForm?.controls.referenceNumber;
        const referenceNumberTypeControl = this.referenceNumberInternationalForm?.controls.referenceNumberType;

        if (!referenceNumberControl || !referenceNumberTypeControl) {
            return;
        }

        combineLatest([
            referenceNumberControl.valueChanges.pipe(
                distinctUntilChanged(),
                filter(value => !!value)
            ),
            referenceNumberTypeControl.valueChanges.pipe(
                distinctUntilChanged(),
                filter(value => !!value)
            )
        ])
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                debounceTime(300),
                filter(([referenceNumber, referenceNumberType]) => !!referenceNumber && !!referenceNumberType),
                tap(([referenceNumber, referenceNumberType]) => {
                    this.loadReferencenNumberData(referenceNumber as string, referenceNumberType);
                })
            )
            .subscribe();
    }

    private loadReferencenNumberData(
        referenceNumber: string,
        type: ReferenceNumberTypeInternational | undefined
    ): void {
        this.store.dispatch(new LoadOrderLineItems(referenceNumber, type));
    }

    private initReferenceNumberFound(): void {
        this.referenceNumberFound$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .pipe(
                tap(found => {
                    this.referenceNumberInternationalForm?.controls.referenceNumberValid.setValue(found);

                    if (found) {
                        this.store.dispatch(new RemoveErrorMessage(WorkflowError.SOURCE_PLANT_OUT_OF_SCOPE));
                    }

                    if (found === false && !this.isQueryingReferenceNumber()) {
                        this.store.dispatch(new AddErrorMessage(WorkflowError.SOURCE_PLANT_OUT_OF_SCOPE));
                    }
                })
            )
            .subscribe();
    }

    private initProductComplaintChanges(): void {
        this.referenceNumberInternationalForm?.controls.productDiscrepancy.valueChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(value => {
                    if (value) {
                        this.store.dispatch(new AddErrorMessage(WorkflowError.SUBMITTED_PRODUCT_COMPLAINTS));
                    } else {
                        this.store.dispatch(new RemoveErrorMessage(WorkflowError.SUBMITTED_PRODUCT_COMPLAINTS));
                    }
                })
            )
            .subscribe();
    }

    private initWithin40DaysChanges(): void {
        this.referenceNumberInternationalForm?.controls.pgiWithin40Days.valueChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(value => {
                    if (!value) {
                        this.store.dispatch(new AddErrorMessage(WorkflowError.REQUEST_FINANCIAL_CREDIT));
                    } else {
                        this.store.dispatch(new RemoveErrorMessage(WorkflowError.REQUEST_FINANCIAL_CREDIT));
                    }
                })
            )
            .subscribe();
    }
}
